import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Unlink, ExternalLink, Loader2 } from "lucide-react";
import { API, nf, df, errMsg } from "@/lib/api";

export const VsAvg = ({ v, testid }) => v == null ? null : (
  <span data-testid={testid} className={`font-mono-num text-[10px] font-semibold px-1 ${v >= 0 ? "bg-[#E6EFE9] text-[#2A4B3C]" : "bg-[#FBF1ED] text-[#C05C3D]"}`}>{v >= 0 ? "+" : ""}{v}% vs media</span>
);

const Metrics = ({ r }) => (
  <div className="grid grid-cols-4 md:grid-cols-7 border-t border-l border-[#E8E8E4] text-center">
    {[["Copertura", r.reach], ["Visualizz.", r.views], ["Reazioni", r.likes], ["Commenti", r.comments], ["Condivisi", r.shares], ["Salvati", r.saves], ["ER", `${r.engagement_rate}%`]].map(([l, v]) => (
      <div key={l} className="border-r border-b border-[#E8E8E4] py-2"><div className="font-mono-num text-sm">{typeof v === "number" ? nf(v) : v}</div><div className="text-[10px] text-[#5C5C58]">{l}</div></div>
    ))}
  </div>
);

export const PostResults = ({ item, onChange }) => {
  const [cands, setCands] = useState(null);
  const [busy, setBusy] = useState(false);
  const results = item.results || [];
  useEffect(() => { setCands(null); }, [item.id]);
  const loadCands = async () => {
    setBusy(true);
    try { setCands((await axios.get(`${API}/calendar/${item.id}/candidates`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setLinks = async (links) => {
    try {
      const r = await axios.put(`${API}/calendar/${item.id}/links`, { links });
      onChange(r.data);
      setCands(null);
      toast.success(links.length ? "Post collegato" : "Collegamento rimosso");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const max = item.platform === "Instagram + Facebook" ? 2 : 1;
  return (
    <div className="border-t border-[#E8E8E4] pt-3 space-y-3 min-w-0" data-testid="post-results">
      <div className="flex justify-between items-center">
        <span className="label !text-[9px]">Risultati del post reale</span>
        {results.length > 0 ? (
          <button data-testid="unlink-post-btn" onClick={() => setLinks([])} className="text-xs text-[#5C5C58] hover:text-[#C05C3D] inline-flex items-center gap-1"><Unlink className="w-3 h-3" />Scollega</button>
        ) : (
          <button data-testid="link-post-btn" onClick={loadCands} className="text-xs font-semibold text-[#2A4B3C] inline-flex items-center gap-1">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />}Collega al post pubblicato</button>
        )}
      </div>
      {results.map((r) => (
        <div key={r.id} className="space-y-2" data-testid={`result-${r.id}`}>
          <div className="flex gap-3 items-start">
            {r.thumbnail && <img src={r.thumbnail} alt="" className="w-12 h-12 object-cover" />}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#5C5C58]">{r.platform} · {r.format} · {df(r.timestamp)} <VsAvg v={r.vs_avg} testid={`result-vsavg-${r.id}`} /></div>
              <div className="text-sm truncate">{r.caption}</div>
            </div>
            {r.permalink && <a href={r.permalink} target="_blank" rel="noreferrer" data-testid={`result-open-${r.id}`}><ExternalLink className="w-4 h-4 text-[#5C5C58] hover:text-[#2A4B3C]" /></a>}
          </div>
          <Metrics r={r} />
        </div>
      ))}
      {!results.length && !cands && <p className="text-xs text-[#5C5C58]">Quando segni il contenuto come pubblicato, l'app lo collega da sola al post pubblicato lo stesso giorno. Puoi anche sceglierlo tu.</p>}
      {cands && (
        <div className="max-h-56 overflow-y-auto divide-y divide-[#E8E8E4] border border-[#E8E8E4]" data-testid="candidate-list">
          {cands.map((c) => (
            <button key={c.id} data-testid={`candidate-${c.id}`} onClick={() => setLinks([c.id, ...results.map((r) => r.id)].slice(0, max))} className="w-full flex gap-3 p-2 text-left hover:bg-[#F7F7F5]">
              {c.thumbnail && <img src={c.thumbnail} alt="" className="w-10 h-10 object-cover" />}
              <div className="flex-1 min-w-0"><div className="text-[11px] text-[#5C5C58]">{c.platform} · {c.format} · {df(c.timestamp)}{c.days_off ? ` (${c.days_off}g di distanza)` : " (stesso giorno)"}</div><div className="text-xs truncate">{c.caption || "(senza testo)"}</div></div>
              <span className="font-mono-num text-xs self-center">{nf(c.interactions)}</span>
            </button>
          ))}
          {!cands.length && <p className="text-xs p-3 text-[#5C5C58]">Nessun post trovato su questa piattaforma.</p>}
        </div>
      )}
    </div>
  );
};
